import axios from 'axios';
import getAuthSession from './auth';
import { REPORT_TYPES, ReportOption, ReportType } from '../interfaces/reports.interface';
import User from '../interfaces/users.interface';
import { QuestionnaireTypes, fetchQuestionnaires, isWorkflowPreFiveThreeOne } from './questionnaires';
import { USER_TYPES } from './user';
import { Theme } from '@mui/material';
import moment from 'moment';
import { AssessmentSurvey } from '../interfaces/assessment.interface';
import { Questionnaire } from '../interfaces/questionnaire.interface';
import getIncluisionConfigurationsById from './inclusionConfiguration';
import { QuestionAnswer } from '../interfaces/questionAnswer.interface';
import { Workflow } from '../interfaces/workflow.interface';
import { BLUE_BADGE_LABEL } from './badge';
import { Question } from '../interfaces/question.interface';

export const buildQuestionsFromDetailedQuestionnaire = (detailedQuestionnaire: Questionnaire, questionnaire: Questionnaire): Question[] => {
  return detailedQuestionnaire.domains.reduce((builtQuestions, d) => {
    d.topics.forEach((t) => {
      t.bestPractices.forEach((bp) => {
        bp.questions.forEach((q) => {
          // Ensure topic, domain, and best practice references
          builtQuestions.push(             {
            ...q,
            topic: t,
            domain: d,
            bestPractice: bp,
            questionnaire: questionnaire,
          },
          );
        });
      });
    });
    return builtQuestions;
  }, [] as Question[]);
};

export async function shouldSkipBp(
  inclusionIds: number[],
  questionAnswers: QuestionAnswer[],
): Promise<boolean> {
  try {
    const { results } = await getIncluisionConfigurationsById(inclusionIds);
    if (!results || results.length === 0) {
      return false;
    }

    return results && results.some(inclusion => !(questionAnswers).some(qa =>
      qa.question === inclusion.question && !!qa?.answer?.length));
  } catch (error) {
    console.error('Error fetching inclusion configurations:', error);
    return false;
  }
}

const convertQuestionnaireTitle = (originTitle: string, titleSuffix: string) => {
  let title = originTitle.replace('Best Practices', '');
  let titlePartial: string = `${title} ${titleSuffix}`;
  if (titlePartial.indexOf('TPN') === -1) titlePartial = `TPN ${titlePartial}`;
  return titlePartial;
};

export const generateConfidentialHeader = (
  companyName: string,
  generatedDate: string,
  surveyType: ReportType = REPORT_TYPES.ASSESSMENT,
  questionnaireTitle?: string,
  reportIssueDate?: string,
): any => {
  const headerText = surveyType === REPORT_TYPES.ASSESSMENT
    ? `${questionnaireTitle} - ${companyName} - Completed: ${reportIssueDate} - Generated ${generatedDate}`
    : `TPN Best Practice Questionnaire: ${companyName} ${generatedDate}`;

  return {
    margin: [40, 10, 40, 5],
    stack: [
      {
        text: 'CONFIDENTIAL',
        color: 'red',
        bold: true,
        fontSize: 11,
        alignment: 'center',
        margin: [0, 0, 0, 2],
      },
      {
        text: headerText,
        alignment: 'center',
        fontSize: 10,
        color: '#333333',
      },
    ],
  };
};

export const generateUnderstandingReportSection = (appTheme: Theme): any[] => {
  return [
    {
      style: 'headerBG',
      margin: [0, 0, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: 'Understanding This Report',
              color: 'white',
              bold: true,
              margin: [5, 5, 5, 5],
            },
          ],
        ],
      },
    },
    {
      margin: [0, 0, 0, 0],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              stack: [
                {
                  margin: [0, 0, 0, 10],
                  lineHeight: 1.2,
                  text: 'The bulk of this report consists of a multi-page chart that establishes: (1) various parties’ opinions on the degree to which the Target Area’s security practices conform to the Best Practices; and (2) what the assessed company intends to do about any identified instances of non-conformance. This chart is organized into the following three sections:',
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 5],
                  text: [
                    { text: '·       ', bold: true },
                    {
                      text: 'Light Blue Columns – Marked “Service Provider”: ',
                      italics: true,
                      bold: true,
                      decoration: 'underline',
                    },
                    'The light blue columns in the chart outline the assessed company’s description of the extent to which its security practices conform to the Best Practices. These responses are self-reported by the company, and do not reflect the independent judgement or findings of a third-party assessor.',
                  ],
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 5],
                  text: [
                    { text: '·       ', bold: true },
                    {
                      text: 'Yellow Columns – Marked “Assessor”: ',
                      italics: true,
                      bold: true,
                      decoration: 'underline',
                    },
                    'The yellow columns in the chart reflect the Assessor’s findings in the TPN assessment. Specifically, they describe the extent to which the Assessor determined, in its independent, third-party review, that the company’s security practices conformed or did not conform to the Best Practices.',
                  ],
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 10],
                  text: [
                    { text: '·       ', bold: true },
                    {
                      text: 'Purple Columns – Marked “Remediation Plans”: ',
                      italics: true,
                      bold: true,
                      decoration: 'underline',
                    },
                    'The purple columns include the assessed company’s statements as to whether it has corrected, intends to correct or does not intend to correct identified non-conformances with the Best Practices. This information is self-reported by the assessed company.',
                  ],
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 0],
                  text: [
                    'In addition, the purple “Comment” column may indicate whether a corrective action as reported by the assessed company would or would not be sufficient to bring the applicable security practice into conformance with the Best Practices and/or Additional Recommendations controls. Where an action’s remediation status is marked as “Reviewed”, it means that, if the action were taken, it would be sufficient to bring the applicable security practice into conformance with the Best Practices and/or Additional Recommendations controls; and where the status is marked as “Rejected”, it means that even if the assessed company were to take the action as described, it would not be sufficient to bring the applicable security practice into conformance with the Best Practices and/or Additional Recommendations controls. ',
                    {
                      text: 'NOTE: TPN does not independently verify that a corrective action has been taken before assigning a status; each status is based solely on the information self-reported by the assessed company.',
                      bold: true,
                      italics: true,
                    },
                  ],
                },
              ],
            },
          ],
        ],
      },
    },
  ];
};

export const generateDisclaimerSectionForSurveyReport = (appTheme: Theme): any[] => {
  return [
    {
      style: 'headerBG',
      margin: [0, 0, 0, 10],
      table: {
        margin: [0, 0, 0, 10],
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: 'Disclaimer',
              color: 'white',
              bold: true,
              margin: [8, 4, 0, 4],
            },
          ],
        ],
      },
      layout: 'noBorders',
    },
    {
      margin: [0, 0, 0, 0],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              stack: [
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 0],
                  text: [
                    'While this report was created under the auspices of TPN’s program, it was produced independently and exclusively by the Company, and reflects the Company’s conclusions and opinions only. ',
                    {
                      text:
                        'TPN WAS NOT INVOLVED IN MAKING THE FINDINGS AND DETERMINATIONS SET FORTH IN THIS REPORT, AND SHALL NOT BE RESPONSIBLE OR LIABLE FOR ANY OF ITS CONTENTS.',
                      bold: true,
                    },
                  ],
                },
              ],
            },
          ],
        ],
      },
    },
  ];
};

export const generateDisclaimerSectionForAssessmentSurvey = (appTheme: Theme): any[] => {
  return [
    {
      style: 'headerBG',
      margin: [0, 0, 0, 10],
      table: {
        margin: [0, 0, 0, 10],
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: 'Disclaimer',
              color: 'white',
              bold: true,
              margin: [8, 4, 0, 4],
            },
          ],
        ],
      },
      layout: 'noBorders',
    },
    {
      margin: [0, 0, 0, 0],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              stack: [
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 0],
                  text: [
                    'While this assessment was performed under the auspices of TPN’s program, the assessment itself was performed independently by the Assessor identified on the cover page. This report reflects that Assessor’s conclusions and opinions only. ',
                    {
                      text: 'TPN WAS NOT INVOLVED IN MAKING THE FINDINGS AND DETERMINATIONS SET FORTH IN THIS REPORT, AND SHALL NOT BE RESPONSIBLE OR LIABLE FOR ANY OF ITS CONTENTS.',
                      bold: true,
                    },
                  ],
                },
              ],
            },
          ],
        ],
      },
    },
  ];
};

export const generateNatureAndScopeSectionForAssessmentSurvey = (
  assessmentPublishDate: string,
  questionnaireVersion: string,
  assessorCompanyName: string,
  appTheme: Theme,
): any[] => {
  return [
    {
      style: 'headerBG',
      margin: [0, 0, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: 'Nature & Scope of This Report',
              color: 'white',
              bold: true,
              margin: [5, 5, 5, 5],
            },
          ],
        ],
      },
    },
    {
      margin: [0, 0, 0, 0],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              stack: [
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 10],
                  text: `This report describes the findings of a point-in-time security assessment completed on ${assessmentPublishDate}. The assessment consisted of a review and evaluation of the security practices used in relation to a particular facility or software application owned by ${assessorCompanyName} (this facility or application, the “Target Area”), using the Motion Picture Association Content Security Best Practices ${questionnaireVersion}[1] (the “Best Practices”) as a baseline. As such, this report describes the Target Area’s security posture in reference to its observed conformance or non-conformance with the security controls outlined in the applicable version of the Best Practices.`,
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 10],
                  text: [
                    {
                      text:
                        'The underlying assessment used a subset of the Best Practices, in which individual Best Practices were included or excluded based on: (1) the type of assessment being performed (i.e., site or application); and (2) their relevance to the business operations of the Target Area (as determined by the Service Provider and the individual performing the assessment (the “Assessor”)). ',
                    },
                    {
                      text:
                        'Any issue that does not relate to the facility or software application identified on the Cover Sheet, or that is not covered by the Best Practices in this report, should be considered ',
                      bold: true,
                      italics: true,
                    },
                    {
                      text: 'outside the scope',
                      bold: true,
                      italics: true,
                      decoration: 'underline',
                    },
                    {
                      text: ' of this assessment.',
                      bold: true,
                      italics: true,
                    },
                  ],
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 10],
                  text: 'Any assessment may be performed on-site or remotely by the Assessor. (The “Assessment Type” row in the Overview Section describes where the assessment was performed.) Where an assessment is performed on-site, the Assessor visited the assessed company’s premises and had an opportunity to review and verify their security practices in person. Where an assessment is performed remotely, the review is limited to reviewing information and materials provided by the assessed company and determining their security posture on that basis.',
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 0],
                  text: '[1] Available at https://www.ttpn.org/links-resources/',
                  link: 'https://www.ttpn.org/links-resources/',
                  decoration: 'underline',
                  color: 'blue',
                },
              ],
            },
          ],
        ],
      },
    },
  ];
};

export const generateNatureAndScopeSectionForSurveyReport = (
  questionnaireSubmittedDate: string,
  questionnaireVersion: string,
  spCompanyName: string,
  appTheme: Theme,
): any[] => {
  return [
    {
      style: 'headerBG',
      margin: [0, 0, 0, 10],
      table: {
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              fillColor: appTheme.palette.primary.main,
              text: 'Nature & Scope of This Report',
              color: 'white',
              bold: true,
              margin: [5, 5, 5, 5],
            },
          ],
        ],
      },
    },
    {
      margin: [0, 0, 0, 0],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [
            {
              border: [false, false, false, false],
              stack: [
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 10],
                  text: [
                    'This report describes the extent to which the security practices used by ',
                    { text: spCompanyName, bold: true },
                    ' (the “Company”), as of ',
                    questionnaireSubmittedDate,
                    ', in regard to the facility or software application identified on the Cover Sheet (this facility or application, the “Target Area”) conform to the Motion Picture Association Content Security Best Practices ',
                    questionnaireVersion,
                    '¹ (the “Best Practices”).',
                  ],
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 10],
                  text: [
                    {
                      text:
                        'Importantly, the information in this report is self-reported by the Company, and does not reflect the independent judgement or findings of a third-party assessor.',
                      bold: true,
                      italics: true,
                    },
                  ],
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 10],
                  text:
                    'In conducting its review, the Company used a subset of the Best Practices, in which individual Best Practices were included or excluded based on the relevance to: (1) the type of assessment being performed (i.e., site or application); and (2) the business operations of the Target Area (as determined by the Service Provider).',
                },
                {
                  lineHeight: 1.2,
                  margin: [0, 0, 0, 0],
                  text: '¹ Available at https://www.ttpn.org/links-resources/',
                  link: 'https://www.ttpn.org/links-resources/',
                  decoration: 'underline',
                  color: 'blue',
                },
              ],
            },
          ],
        ],
      },
    },
  ];
};

export const generateSharedSectionIntro = (): any[] => {
  return [
    {
      text: [
        {
          text: 'IMPORTANT',
          decoration: 'underline',
          bold: true,
          italics: true,
          lineHeight: 1.2,
        },
        {
          text: ': Before reviewing this report, please review this Section, as it contains important information regarding how to read and understand this document.',
          italics: true,
          lineHeight: 1.2,
        },
      ],
      alignment: 'center',
      margin: [0, 0, 0, 20],
    },
  ];
};

export const generateHorizontalLine = (width: number): any[] => {
  return [
    {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: width,
          y2: 0,
          lineWidth: 0.5,
          lineColor: '#cccccc',
        },
      ],
      margin: [0, 25, 0, 25],
    },
  ];
};

export const setAltBadgeImage = (wf: Workflow) => {
  return  isWorkflowPreFiveThreeOne(wf.workflowType) ? BLUE_BADGE_LABEL : 'nextgen_tpn_self_reported';
};

export const surveyReportLegalLanguageSection = (
  badgeDate: string,
  companyName: string,
  questionnaire: Questionnaire,
  appTheme: Theme,
): any => {
  return {
    margin: [0, 10, 0, 30],
    border: [true, true, true, true],
    table: {
      widths: ['*'],
      margin: [0, 30],
      body: [
        [
          {
            stack: [
              ...generateSharedSectionIntro(),
              ...generateNatureAndScopeSectionForSurveyReport(
                badgeDate,
                questionnaire.title,
                companyName,
                appTheme,
              ),
              ...generateHorizontalLine(495),
              ...generateDisclaimerSectionForSurveyReport(appTheme),
            ],
            margin: [10, 10, 10, 0],
          },
        ],
      ],
    },
  };
};

export const assessmentReportLegalLanguageSection = (
  assessmentSurvey: AssessmentSurvey,
  questionnaire: Questionnaire,
  appTheme: Theme,
  assessorCompanyName: string,
): any => {
  return {
    margin: [0, 10, 0, 30],
    border: [true, true, true, true],
    table: {
      widths: ['*'],
      margin: [0, 30],
      body: [
        [
          {
            stack: [
              ...generateSharedSectionIntro(),
              ...generateNatureAndScopeSectionForAssessmentSurvey(
                moment(assessmentSurvey.reportIssueDate).format('MMM DD, YYYY'),
                questionnaire.title,
                assessorCompanyName,
                appTheme,
              ),
              ...generateHorizontalLine(715),
              ...generateUnderstandingReportSection(appTheme),
              ...generateHorizontalLine(715),
              ...generateDisclaimerSectionForAssessmentSurvey(appTheme),
            ],
            margin: [10, 10, 10, 0],
          },
        ],
      ],
    },
  };
};

export default async function fetchReportOptions(user?: User) {
  let keyIndex = 1;  // Starting at 1 to avoid 0 as a key.

  // TEMP - Hide reports from all but TPN Admins
  if (user?.type !== USER_TYPES.TPN_ADMIN) return [];
  // END TEMP

  const ReportOptions: ReportOption[] = [
    { reportName: 'TPN Assessments Status', reportUrl: 'tpn-service-provider-assessments-status-report', reportKey: keyIndex++ },
  ];

  const questionnaires = await fetchQuestionnaires(QuestionnaireTypes.MPA_BEST_PRACTICE);
  questionnaires.forEach((questionnaire) => {
    if (questionnaire.includeInReporting) {
      let reportName: string = convertQuestionnaireTitle(questionnaire.title, 'Assessor Findings');
      if (!ReportOptions.find((reportOption) => reportOption.reportName === reportName)) {
        ReportOptions.push(
          {
            reportName: reportName,
            reportUrl: 'assessment-survey-report',
            urlKwargs: { questionnaireId: questionnaire.id },
            reportKey: keyIndex++,
          },
        );
      }
    }
  });
  questionnaires.forEach((questionnaire) => {
    let reportName: string = convertQuestionnaireTitle(questionnaire.title, 'Service Provider Answers');
    if (!ReportOptions.find((reportOption) => reportOption.reportName === reportName)) {
      ReportOptions.push(
        {
          reportName: reportName,
          reportUrl: 'service-provider-bp-answers-report',
          urlKwargs: { questionnaireId: questionnaire.id },
          reportKey: keyIndex++,
        });
    }
  });
  if (user?.type === USER_TYPES.TPN_ADMIN) {
    ReportOptions.push({
      reportName: 'All Companies Report',
      reportUrl: 'all-companies-report',
      reportKey: keyIndex++,
    });
    ReportOptions.push({
      reportName: 'Assessments Rollup Report',
      reportUrl: 'assessments-rollup-report',
      reportKey: keyIndex++,
    });
    ReportOptions.push({
      reportName: 'Assessments Omni Report',
      reportUrl: 'assessments-omni-report',
      reportKey: keyIndex++,
    });
    ReportOptions.push({
      reportName: 'Partner Resources Individual Downloads Report',
      reportUrl: 'partner-resources-individual-downloads-report',
      reportKey: keyIndex++,
    });
    ReportOptions.push({
      reportName: 'Partner Resources Individual Downloads Rollup Report',
      reportUrl: 'partner-resources-individual-downloads-rollup-report',
      reportKey: keyIndex++,
    });
    ReportOptions.push({
      reportName: 'Weekly Blue Badge Audit Report',
      reportUrl: 'blue-badge-audit-report',
      reportKey: keyIndex++,
    });
    questionnaires.forEach((questionnaire) => {
      if (questionnaire.includeInReporting) {
        let reportName: string = convertQuestionnaireTitle(questionnaire.title, 'Monthly Regions Report');
        if (!ReportOptions.find((reportOption) => reportOption.reportName === reportName)) {
          ReportOptions.push(
            {
              reportName: reportName,
              reportUrl: 'monthly-assessment-report',
              urlKwargs: { questionnaireId: questionnaire.id },
              reportKey: keyIndex++,
            },
          );
        }
      }
    });
  }

  questionnaires.forEach((questionnaire) => {
    let reportName: string = '';
    if (questionnaire.title.includes('Best Practices') && questionnaire.title.includes('v5.')) {
      let versionStr: string = questionnaire.title.split('v5.')[1];
      reportName = `TPN Questionnaire v5.${versionStr} Remediation Plans`;
    } else {
      reportName = `TPN Questionnaire ${questionnaire.title} Remediation Plans`;
    }

    if (!ReportOptions.find((reportOption) => reportOption.reportName === reportName)) {
      ReportOptions.push(
        {
          reportName: reportName,
          reportUrl: 'gold-remediation-status-report',
          urlKwargs: { questionnaireId: questionnaire.id },
          reportKey: keyIndex++,
        },
      );
    }
  });

  return ReportOptions;
}

export async function fetchReport(reportOption: ReportOption) {
  const authSession = await getAuthSession();
  let url = '';
  if (reportOption.urlKwargs) {
    if (reportOption.reportUrl === 'assessment-survey-report') url = `${process.env.REACT_APP_BASE_API}/assessment-surveys/questionnaire/${reportOption.urlKwargs?.questionnaireId}/reporting/csv/`;
    if (reportOption.reportUrl === 'service-provider-bp-answers-report') url = `${process.env.REACT_APP_BASE_API}/reports/service-provider-bp-answers-report/${reportOption.urlKwargs?.questionnaireId}/csv/`;
    if (reportOption.reportUrl === 'gold-remediation-status-report') url = `${process.env.REACT_APP_BASE_API}/reports/gold-remediation-status-report/${reportOption.urlKwargs?.questionnaireId}/csv/`;
    if (reportOption.reportUrl === 'monthly-assessment-report') url = `${process.env.REACT_APP_BASE_API}/reports/${reportOption.reportUrl}/${reportOption.urlKwargs?.questionnaireId}/csv/`;
  } else if (reportOption.reportUrl === 'tpn-service-provider-assessments-status-report') url = `${process.env.REACT_APP_BASE_API}/reports/assessments/csv/`;
  else {
    url = `${process.env.REACT_APP_BASE_API}/reporting/${reportOption.reportUrl}/`;
  }
  const response = await axios.get(
    url,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    });
  return response.data;
}
